import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SupplierService } from './supplier.service';
import { SupplierProfile } from './supplier.type';
import { Header } from '../shared/header/header';
import { AuthService } from '../auth/auth.service';

/**
 * Supplier management: priority, the active manual-contract allowance, and the
 * enabled flag. These three are admin-only — no supplier-facing endpoint can
 * reach them.
 */
@Component({
  selector: 'app-supplier',
  imports: [
    CommonModule, FormsModule, MatTableModule, MatButtonModule, MatIconModule,
    MatInputModule, MatFormFieldModule, MatSlideToggleModule,
    MatProgressSpinnerModule, MatTooltipModule, MatPaginatorModule, Header,
  ],
  templateUrl: './supplier.html',
  styleUrl: './supplier.scss',
})
export class Supplier implements OnInit {
  readonly canEdit = inject(AuthService).canWrite('suppliers_payments');
  cols = ['name', 'username', 'phone', 'priority', 'allowance', 'enabled', 'actions'];
  suppliers: SupplierProfile[] = [];
  loading = false;
  error: string | null = null;
  search = '';

  pageIndex = 0;
  pageSize = 20;
  total = 0;
  readonly pageSizeOptions = [10, 20, 50, 100];

  /** Pending edits, keyed by user id, so a row only saves what changed. */
  drafts = new Map<string, { priority: number; allowance: number }>();

  constructor(private service: SupplierService, private snackBar: MatSnackBar) { }

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.error = null;
    this.service.listSuppliers({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      search: this.search.trim() || undefined,
    }).subscribe({
      next: res => {
        this.suppliers = res.suppliers;
        this.total = res.total;
        this.drafts.clear();
        res.suppliers.forEach(s => this.drafts.set(s.user_id, {
          priority: s.priority,
          allowance: s.max_active_manual_contracts,
        }));
        this.loading = false;
      },
      error: err => {
        this.error = err?.error?.error ?? 'Failed to load suppliers';
        this.loading = false;
      },
    });
  }

  onSearch(): void {
    this.pageIndex = 0;
    this.load();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.load();
  }

  draft(id: string) {
    return this.drafts.get(id)!;
  }

  dirty(s: SupplierProfile): boolean {
    const d = this.drafts.get(s.user_id);
    if (!d) return false;
    return d.priority !== s.priority || d.allowance !== s.max_active_manual_contracts;
  }

  save(s: SupplierProfile): void {
    const d = this.drafts.get(s.user_id)!;
    this.service.updateSupplier(s.user_id, {
      priority: d.priority,
      max_active_manual_contracts: d.allowance,
    }).subscribe({
      next: () => {
        this.snackBar.open(`Saved ${s.name}`, 'Close', { duration: 3000 });
        this.load();
      },
      error: err => this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 }),
    });
  }

  toggleEnabled(s: SupplierProfile, enabled: boolean): void {
    this.service.updateSupplier(s.user_id, { enabled }).subscribe({
      next: () => {
        this.snackBar.open(
          enabled
            ? `${s.name} enabled`
            : `${s.name} disabled — they cannot arm accounts or open contracts, but work already running is unaffected.`,
          'Close', { duration: 5000 });
        this.load();
      },
      error: err => {
        this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 });
        this.load();
      },
    });
  }
}
